// ─────────────────────────────────────────────────────────────────────────────
// Decision-support candidates for PAR Intelligence action drawers.
//
// A small set of insight-card actions ask the user to pick between a handful of
// ranked options (which gift type to swap in, who reviews a flagged store, how
// broad an audit should run, etc.) instead of filling in a blank field. This
// module is the single registry mapping an insight-action id to the candidates
// it should show, keyed the same way `campaignActions.ts` keys campaign
// operations.
//
// MOCK DATA: none of these decisions have a backend ranking/evidence endpoint
// yet. Offer-swap and win-back candidates use real gift-type names/ids from
// `/api/CampaignEditor/promotions` with mocked evidence merged on; the other
// three decisions (review routing, audit scope, schedule scope) are fully
// mocked. See the implementation summary for the backend tickets this implies.
// ─────────────────────────────────────────────────────────────────────────────

import {getPromotions} from "../../pages/campaign-editor/campaignAPI";
import type {Promotion} from "../../pages/campaign-editor/campaignAPI";

export interface CandidateFact {
    label: string;
    value: string;
}

export interface CandidateProjection {
    /** e.g. "Projected redemption rate" */
    headline: string;
    /** e.g. "9.2%" */
    value: string;
    /** e.g. "+5.0 pts vs. 4.2% today" */
    deltaLabel?: string;
    /** e.g. "+$5,100/wk margin recovered" */
    secondary?: string;
    /** Optional historical trend for the sparkline, oldest to newest. */
    curve?: number[];
}

export interface DecisionCandidate {
    id: string;
    name: string;
    description?: string;
    recommended: boolean;
    /** One-line reason the AI recommends (or doesn't recommend) this option. */
    whyLine: string;
    facts: CandidateFact[];
    segments?: string[];
    projection: CandidateProjection;
    /** Present when the candidate maps to a real Punchh gift type. */
    redeemableUuid?: string;
}

/** Hardcoded framing shown above the candidate cards — insight, action, goal. */
export interface DecisionSupportContext {
    insight: string;
    action: string;
    goal: string;
}

export interface DecisionSupportConfig {
    actionId: string;
    /** Id of the hidden editableField this decision is bridged through for chat. */
    fieldId: string;
    fieldLabel: string;
    cardsSectionTitle: string;
    /** Bullet-derived field label this decision replaces, if any. */
    replacesBulletLabel?: string;
    /** Static fallback if the LLM context call fails. */
    fallbackContext: DecisionSupportContext;
    loadCandidates: (signal?: AbortSignal) => Promise<DecisionCandidate[]>;
    skipLabel: string;
}

function mockDelay<T>(value: T, ms: number, signal?: AbortSignal): Promise<T> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => resolve(value), ms);
        signal?.addEventListener("abort", () => {
            clearTimeout(timer);
            reject(new DOMException("Aborted", "AbortError"));
        });
    });
}

// ── Gift type swap (Offer Effectiveness card) ────────────────────────────────

interface MockEvidenceProfile {
    recommended: boolean;
    whyLine: string;
    facts: CandidateFact[];
    segments?: string[];
    projection: CandidateProjection;
}

// MOCK: illustrative redemption evidence — no ranking API exists yet. No margin
// figures: the loyalty data has no holdout, so recovered margin is unknowable.
const PROMO_SWAP_EVIDENCE: MockEvidenceProfile[] = [
    {
        recommended: true,
        whyLine: "Redeemed at 9.2% with this same segment in March.",
        facts: [{label: "Historical redemption", value: "9.2%"}],
        segments: ["Loyalty members", "First-time guests"],
        projection: {
            headline: "Projected redemption rate",
            value: "9.2%",
            deltaLabel: "+5.0 pts vs. 4.2% today",
            curve: [3.6, 3.9, 4.4, 5.8, 7.1, 8.4, 9.2]
        }
    },
    {
        recommended: false,
        whyLine: "Ran at 6.8% with a broader, lower-loyalty audience last fall.",
        facts: [{label: "Historical redemption", value: "6.8%"}],
        segments: ["General guests"],
        projection: {
            headline: "Projected redemption rate",
            value: "6.8%",
            deltaLabel: "+2.6 pts vs. 4.2% today",
            curve: [3.6, 3.8, 4.1, 4.9, 5.7, 6.2, 6.8]
        }
    },
    {
        recommended: false,
        whyLine: "A smaller draw historically, but the cheapest offer to fund.",
        facts: [{label: "Historical redemption", value: "5.1%"}],
        segments: ["Loyalty members"],
        projection: {
            headline: "Projected redemption rate",
            value: "5.1%",
            deltaLabel: "+0.9 pts vs. 4.2% today",
            curve: [3.6, 3.7, 3.9, 4.2, 4.6, 4.9, 5.1]
        }
    }
];

// MOCK: fallback names/ids used only if /api/CampaignEditor/promotions is
// unavailable or returns fewer than 3 promotions.
const FALLBACK_PROMOTIONS: Promotion[] = [
    {
        redeemableUuid: "mock-promo-free-dessert",
        name: "Free Dessert Add-On",
        description: "Free dessert with any entrée purchase"
    },
    {
        redeemableUuid: "mock-promo-2-off-combo",
        name: "$2 Off Combo",
        description: "$2 off any combo meal"
    },
    {
        redeemableUuid: "mock-promo-free-drink",
        name: "Free Small Drink",
        description: "Free small drink with any purchase"
    }
];

function candidatesFromPromotions(
    promotions: Promotion[],
    evidenceProfiles: MockEvidenceProfile[]
): DecisionCandidate[] {
    return promotions.map((promo, index) => {
        const evidence =
            evidenceProfiles[index] ?? evidenceProfiles[evidenceProfiles.length - 1];
        return {
            id: promo.redeemableUuid,
            name: promo.name,
            description: promo.description,
            redeemableUuid: promo.redeemableUuid,
            recommended: evidence.recommended,
            whyLine: evidence.whyLine,
            facts: evidence.facts,
            segments: evidence.segments,
            projection: evidence.projection
        };
    });
}

async function loadPromotionSwapCandidates(
    signal?: AbortSignal
): Promise<DecisionCandidate[]> {
    let promotions: Promotion[];
    try {
        const fetched = await getPromotions(signal);
        promotions = fetched.length >= 3 ? fetched.slice(0, 3) : FALLBACK_PROMOTIONS;
    } catch {
        promotions = FALLBACK_PROMOTIONS;
    }
    return candidatesFromPromotions(promotions, PROMO_SWAP_EVIDENCE);
}

// ── Win-back offer (Loyalty Churn card) ──────────────────────────────────────

// MOCK: illustrative recovery-rate evidence — no ranking API exists yet.
const CHURN_OFFER_EVIDENCE: MockEvidenceProfile[] = [
    {
        recommended: true,
        whyLine: "Recovered 18% of similar lapsed mid-tier members last quarter.",
        facts: [
            {label: "Historical recovery rate", value: "18%"},
            {label: "Revenue recovered", value: "~$34 at avg. check"}
        ],
        segments: ["Mid-tier (100–300 pts)", "45–60 days inactive"],
        projection: {
            headline: "Projected recovery rate",
            value: "18%",
            deltaLabel: "vs. 6% baseline win-back rate",
            secondary: "~$34K revenue recaptured",
            curve: [4, 6, 9, 12, 15, 17, 18]
        }
    },
    {
        recommended: false,
        whyLine: "Broader reach, but recovered fewer members per dollar last time.",
        facts: [
            {label: "Historical recovery rate", value: "12%"},
            {label: "Revenue recovered", value: "~$22 at avg. check"}
        ],
        segments: ["All at-risk tiers"],
        projection: {
            headline: "Projected recovery rate",
            value: "12%",
            deltaLabel: "vs. 6% baseline win-back rate",
            secondary: "~$22 revenue recaptured",
            curve: [4, 5, 7, 9, 10, 11, 12]
        }
    },
    {
        recommended: false,
        whyLine: "Cheapest to fund, but the smallest lift seen historically.",
        facts: [
            {label: "Historical recovery rate", value: "8%"},
            {label: "Revenue recovered", value: "~$14 at avg. check"}
        ],
        segments: ["Top-tier (500+ pts)"],
        projection: {
            headline: "Projected recovery rate",
            value: "8%",
            deltaLabel: "vs. 6% baseline win-back rate",
            secondary: "~$14 revenue recaptured",
            curve: [4, 4.5, 5.5, 6.5, 7.2, 7.7, 8]
        }
    }
];

// MOCK: fallback names/ids used only if /api/CampaignEditor/promotions is
// unavailable or returns fewer than 3 redeemables.
const FALLBACK_CHURN_OFFERS: Promotion[] = [
    {
        redeemableUuid: "mock-offer-double-points",
        name: "Double Points Reload",
        description: "2x points on next visit"
    },
    {
        redeemableUuid: "mock-offer-free-item",
        name: "Free Item Reward",
        description: "Free menu item, no purchase required"
    },
    {
        redeemableUuid: "mock-offer-5-credit",
        name: "$5 Welcome Back Credit",
        description: "$5 credit toward next order"
    }
];

async function loadWinBackOfferCandidates(
    signal?: AbortSignal
): Promise<DecisionCandidate[]> {
    let offers: Promotion[];
    try {
        const fetched = await getPromotions(signal);
        offers = fetched.length >= 3 ? fetched.slice(0, 3) : FALLBACK_CHURN_OFFERS;
    } catch {
        offers = FALLBACK_CHURN_OFFERS;
    }
    return candidatesFromPromotions(offers, CHURN_OFFER_EVIDENCE);
}

// ── Review routing (Store Health card) ───────────────────────────────────────

// MOCK: no task-routing/turnaround API exists yet; illustrative demo data only.
async function loadReviewRoutingCandidates(
    signal?: AbortSignal
): Promise<DecisionCandidate[]> {
    const candidates: DecisionCandidate[] = [
        {
            id: "route_store_gm",
            name: "Store GM — on-site review",
            recommended: true,
            whyLine:
                "Closed the last 4 manager-review tasks at this store within 3 days.",
            facts: [
                {label: "Avg. turnaround", value: "3 days"},
                {label: "Open tasks", value: "2"}
            ],
            projection: {
                headline: "Projected turnaround",
                value: "3 days",
                deltaLabel: "Fastest of the 3 options",
                secondary: "On-site — sees the pattern first-hand"
            }
        },
        {
            id: "route_district_manager",
            name: "District Manager — second review",
            recommended: false,
            whyLine: "Adds oversight across all 8 stores in the district.",
            facts: [
                {label: "Avg. turnaround", value: "6 days"},
                {label: "Open tasks", value: "5"}
            ],
            projection: {
                headline: "Projected turnaround",
                value: "6 days",
                deltaLabel: "+3 days vs. store GM",
                secondary: "Broader context across the district"
            }
        },
        {
            id: "route_both",
            name: "Store GM, District Manager cc'd",
            recommended: false,
            whyLine: "Keeps the GM accountable while giving the DM visibility.",
            facts: [
                {label: "Avg. turnaround", value: "3–4 days"},
                {label: "Open tasks", value: "2 (GM)"}
            ],
            projection: {
                headline: "Projected turnaround",
                value: "3–4 days",
                deltaLabel: "Near-fastest, with a second set of eyes",
                secondary: "Best of both — slightly more coordination"
            }
        }
    ];
    return mockDelay(candidates, 250, signal);
}

// ── Audit scope (High Food Cost card) ────────────────────────────────────────

// MOCK: no intervention-outcome API exists yet; illustrative demo data only.
async function loadAuditScopeCandidates(
    signal?: AbortSignal
): Promise<DecisionCandidate[]> {
    const candidates: DecisionCandidate[] = [
        {
            id: "audit_next_evening_shift",
            name: "Next evening shift only",
            recommended: false,
            whyLine:
                "Matches the exact shift where 8 of 12 high-variance events occurred.",
            facts: [
                {label: "Recovers", value: "~$2,100/wk of the $3,200 gap"},
                {label: "Signal within", value: "2 days"}
            ],
            projection: {
                headline: "Projected weekly recovery",
                value: "$2,100/wk",
                deltaLabel: "66% of the $3,200 gap",
                secondary: "Signal within 2 days — lowest effort",
                curve: [0, 0, 600, 1200, 1700, 2100]
            }
        },
        {
            id: "audit_full_week",
            name: "Full week, all shifts",
            recommended: true,
            whyLine:
                "Covers day shifts too, in case the pattern isn't purely evening.",
            facts: [
                {label: "Recovers", value: "~$3,000/wk of the $3,200 gap"},
                {label: "Signal within", value: "7 days"}
            ],
            projection: {
                headline: "Projected weekly recovery",
                value: "$3,000/wk",
                deltaLabel: "94% of the $3,200 gap",
                secondary: "Signal within 7 days — highest effort",
                curve: [0, 300, 900, 1600, 2200, 2700, 3000]
            }
        },
        {
            id: "audit_message_retrain",
            name: "Message + retrain, no formal audit",
            recommended: false,
            whyLine:
                "Fastest to act on, but lower confidence without a documented audit.",
            facts: [
                {label: "Recovers", value: "~$1,200/wk of the $3,200 gap"},
                {label: "Signal within", value: "Same day"}
            ],
            projection: {
                headline: "Projected weekly recovery",
                value: "$1,200/wk",
                deltaLabel: "38% of the $3,200 gap",
                secondary: "Fastest to act, lower confidence",
                curve: [0, 200, 500, 800, 1000, 1200]
            }
        }
    ];
    return mockDelay(candidates, 250, signal);
}

// ── Schedule scope (Menu Mix & Labor / Speed of Service card) ────────────────

// MOCK: no scheduling-simulation API exists yet; illustrative demo data only.
async function loadScheduleScopeCandidates(
    signal?: AbortSignal
): Promise<DecisionCandidate[]> {
    const candidates: DecisionCandidate[] = [
        {
            id: "schedule_this_location_plus1",
            name: "This location only, +1 production",
            recommended: true,
            whyLine:
                "This store's grill bottleneck matches the exact profile flagged last Tuesday.",
            facts: [
                {label: "Projected ticket time", value: "2:30 avg (from 4:45)"},
                {label: "Added labor cost", value: "+$180/wk"}
            ],
            projection: {
                headline: "Projected grill ticket time",
                value: "2:30",
                deltaLabel: "-2:15 vs. 4:45 today",
                secondary: "+$180/wk added labor cost",
                curve: [4.75, 4.2, 3.6, 3.0, 2.5]
            }
        },
        {
            id: "schedule_all_lto_locations_plus1",
            name: "All Level-3+ LTO locations, +1 production",
            recommended: false,
            whyLine:
                "Covers every store running the same high-complexity LTO, not just this one.",
            facts: [
                {label: "Projected ticket time", value: "2:35 avg (from 4:45)"},
                {label: "Added labor cost", value: "+$1,400/wk across locations"}
            ],
            projection: {
                headline: "Projected grill ticket time",
                value: "2:35",
                deltaLabel: "-2:10 vs. 4:45 today",
                secondary: "+$1,400/wk added labor cost",
                curve: [4.75, 4.25, 3.65, 3.05, 2.58]
            }
        },
        {
            id: "schedule_this_location_plus2",
            name: "This location, +2 production",
            recommended: false,
            whyLine:
                "Fastest recovery to target, at a higher labor cost for one store.",
            facts: [
                {label: "Projected ticket time", value: "2:00 avg (from 4:45)"},
                {label: "Added labor cost", value: "+$360/wk"}
            ],
            projection: {
                headline: "Projected grill ticket time",
                value: "2:00",
                deltaLabel: "-2:45 vs. 4:45 today, hits target",
                secondary: "+$360/wk added labor cost",
                curve: [4.75, 4.0, 3.2, 2.4, 2.0]
            }
        }
    ];
    return mockDelay(candidates, 250, signal);
}

// ── Labor schedule adjustment (Speed of Service — Review Schedule action) ────

async function loadLaborScheduleAdjustCandidates(
    signal?: AbortSignal
): Promise<DecisionCandidate[]> {
    const candidates: DecisionCandidate[] = [
        {
            id: "labor_flagged_window_days",
            name: "Flagged window days — add coverage",
            recommended: true,
            whyLine:
                "Targets the days and hours where SOS exceeded target, without reshaping the whole week.",
            facts: [
                {label: "Focus", value: "Days that overlap the flagged SOS window"},
                {
                    label: "Change type",
                    value: "Add crew where scheduled hours run below need"
                }
            ],
            projection: {
                headline: "Projected SOS in flagged window",
                value: "Move toward target",
                deltaLabel: "Lowest schedule disruption",
                secondary: "Patch the window that drove the insight"
            }
        },
        {
            id: "labor_peak_block_only",
            name: "Peak block only — one added shift",
            recommended: false,
            whyLine:
                "Adds a single shift during the worst part of the window; leaves the rest of the week untouched.",
            facts: [
                {label: "Focus", value: "Peak hours inside the flagged window"},
                {label: "Change type", value: "One added shift block during peak"}
            ],
            projection: {
                headline: "Projected peak wait",
                value: "Shorter peak waits",
                deltaLabel: "Smallest labor add",
                secondary: "Fastest edit on the schedule"
            }
        },
        {
            id: "labor_full_week_rebalance",
            name: "Full week — rebalance crew and managers",
            recommended: false,
            whyLine:
                "Use when the shortfall may not be isolated — spreads fixes across the full schedule week.",
            facts: [
                {label: "Focus", value: "Entire schedule week at this location"},
                {
                    label: "Change type",
                    value: "Rebalance crew and manager coverage by day"
                }
            ],
            projection: {
                headline: "Projected weekly coverage",
                value: "More even deployment",
                deltaLabel: "Broadest fix",
                secondary: "Higher coordination effort"
            }
        }
    ];
    return mockDelay(candidates, 250, signal);
}

// ── Report grouping (Store Health — Review Log action) ───────────────────────

async function loadReportGroupingCandidates(
    signal?: AbortSignal
): Promise<DecisionCandidate[]> {
    const candidates: DecisionCandidate[] = [
        {
            id: "report_group_employee",
            name: "Group by employee",
            recommended: true,
            whyLine:
                "Surfaces whether a small set of employees is driving the elevated KPI vs. the store average.",
            facts: [
                {label: "Best for", value: "Outlier employees or registers"},
                {label: "Opens with", value: "Who stands out vs. store baseline"}
            ],
            projection: {
                headline: "Projected finding",
                value: "Named outliers",
                deltaLabel: "Fastest path to a coaching conversation",
                secondary: "Pairs with void, waste, and comp signals"
            }
        },
        {
            id: "report_group_day",
            name: "Group by day",
            recommended: false,
            whyLine:
                "Shows whether the pattern is sustained across the window or concentrated on one or two days.",
            facts: [
                {label: "Best for", value: "Shift or day-of-week patterns"},
                {label: "Opens with", value: "When the drift started and persisted"}
            ],
            projection: {
                headline: "Projected finding",
                value: "Day-level trend",
                deltaLabel: "Good when KPIs drifted together",
                secondary: "Helps separate a bad day from a systemic issue"
            }
        },
        {
            id: "report_group_location",
            name: "Group by location",
            recommended: false,
            whyLine:
                "Compares this store to peers when you are reviewing above store level or multiple sites.",
            facts: [
                {label: "Best for", value: "Multi-site or district review"},
                {
                    label: "Opens with",
                    value: "Whether this store is an outlier in the set"
                }
            ],
            projection: {
                headline: "Projected finding",
                value: "Relative rank",
                deltaLabel: "Broader context",
                secondary: "Useful when the insight spans a group node"
            }
        }
    ];
    return mockDelay(candidates, 250, signal);
}

// ── Registry ──────────────────────────────────────────────────────────────────

export const DECISION_SUPPORT_BY_ACTION_ID: Record<string, DecisionSupportConfig> = {
    act_demo_promo_promotion: {
        actionId: "act_demo_promo_promotion",
        fieldId: "candidate_promotion",
        fieldLabel: "New gift type",
        cardsSectionTitle: "Choose the new gift type",
        replacesBulletLabel: "Swap to",
        fallbackContext: {
            insight:
                "BOGO chicken sandwich is at 4.2% redemption — below the 7% bar — and 61% of redemptions are loyalty members who'd visit anyway (~$4,200/wk in recoverable margin).",
            action: "Swap the BOGO gift type for one with a stronger draw for the same audience.",
            goal: "Lift redemption above the 7% threshold without losing the 8% traffic lift."
        },
        loadCandidates: loadPromotionSwapCandidates,
        skipLabel: "Skip — take me to the campaign editor"
    },
    act_demo_churn_campaign: {
        actionId: "act_demo_churn_campaign",
        fieldId: "candidate_winback_offer",
        fieldLabel: "Win-back offer",
        cardsSectionTitle: "Choose the win-back offer",
        replacesBulletLabel: "Offer",
        fallbackContext: {
            insight:
                "8,400 members are 45+ days inactive after visiting 2×/mo — up 22% from last quarter, with mid-tier (100–300 pts) aging out fastest.",
            action: "Launch a win-back campaign to the 8,400 at-risk members with an offer before they hit 60 days.",
            goal: "Recover ~18% of the pool (~$34K) based on historical win-back performance."
        },
        loadCandidates: loadWinBackOfferCandidates,
        skipLabel: "Skip — take me to the campaign editor"
    },
    act_demo_health_assign_review: {
        actionId: "act_demo_health_assign_review",
        fieldId: "candidate_review_routing",
        fieldLabel: "Route review to",
        cardsSectionTitle: "Choose who reviews this",
        fallbackContext: {
            insight:
                "Void, refund, and discount signals are drifting up at this store vs. its 90-day baseline.",
            action: "Assign a manager-level review of the trend.",
            goal: "Confirm whether it's process, training, or staffing — before it compounds."
        },
        loadCandidates: loadReviewRoutingCandidates,
        skipLabel: "Skip — assign with AI's recommended reviewer"
    },
    act_demo_health_review_log: {
        actionId: "act_demo_health_review_log",
        fieldId: "candidate_report_grouping",
        fieldLabel: "Report grouping",
        cardsSectionTitle: "Choose how to open the report",
        replacesBulletLabel: "Breakdown",
        fallbackContext: {
            insight:
                "One or more store-health KPIs — waste, meal comps, voids, discounts — are elevated vs. this location's baseline.",
            action: "Open the transaction report for the flagged window to see what's driving the drift.",
            goal: "Separate routine activity from anything that needs a manager's closer look."
        },
        loadCandidates: loadReportGroupingCandidates,
        skipLabel: "Skip — open the report with default grouping"
    },
    act_demo_foodcost_audit: {
        actionId: "act_demo_foodcost_audit",
        fieldId: "candidate_audit_scope",
        fieldLabel: "Audit scope",
        cardsSectionTitle: "Choose the audit scope",
        replacesBulletLabel: "When",
        fallbackContext: {
            insight:
                "322 S Broadway is 8.4% over theoretical food cost ($3,200 avoidable) — Classic Chicken Sandwich usage is 28% above standard, concentrated on the evening shift.",
            action: "Assign a portioning audit at 322 S Broadway focused on the Classic Chicken Sandwich during evening shift (4pm–close).",
            goal: "Verify portion control against recipe standard and close the majority of the variance gap."
        },
        loadCandidates: loadAuditScopeCandidates,
        skipLabel: "Skip — create the audit checklist with AI defaults"
    },
    act_demo_sos_schedule: {
        actionId: "act_demo_sos_schedule",
        fieldId: "candidate_schedule_scope",
        fieldLabel: "Schedule change scope",
        cardsSectionTitle: "Choose the schedule change",
        replacesBulletLabel: "Change",
        fallbackContext: {
            insight:
                "Speed of service broke down in a peak window — high-complexity menu mix met under-deployed labor at the grill.",
            action: "Adjust the scheduling template for high-complexity periods.",
            goal: "Add production before lunch peak so ticket times stay on target."
        },
        loadCandidates: loadScheduleScopeCandidates,
        skipLabel: "Skip — apply AI's recommended schedule change"
    },
    act_demo_sos_labor_schedule: {
        actionId: "act_demo_sos_labor_schedule",
        fieldId: "candidate_labor_adjustment",
        fieldLabel: "Schedule adjustment",
        cardsSectionTitle: "Choose how to adjust the schedule",
        replacesBulletLabel: "Goal",
        fallbackContext: {
            insight:
                "Drive-thru speed of service exceeded target during the flagged window — guest experiences were impacted despite crew on shift.",
            action: "Review the labor schedule for the flagged window and adjust coverage where it falls short.",
            goal: "Add staffing where the shortfall lines up with the slowdown and move SOS back toward target."
        },
        loadCandidates: loadLaborScheduleAdjustCandidates,
        skipLabel: "Skip — open the schedule with default view"
    }
};

// ── Chat bridge helpers ──────────────────────────────────────────────────────

/** Evidence-embedded option label so the modify-workflow LLM can re-rank/filter
 * candidates using the same facts shown on the cards (e.g. "higher margin"). */
export function formatCandidateOption(candidate: DecisionCandidate): string {
    const primaryFact = candidate.facts[0];
    return primaryFact
        ? `${candidate.name} — ${
              primaryFact.value
          } ${primaryFact.label.toLowerCase()}`
        : candidate.name;
}

export function parseCandidateFromOption(
    option: string,
    candidates: DecisionCandidate[]
): DecisionCandidate | undefined {
    return candidates.find(
        (candidate) => formatCandidateOption(candidate) === option
    );
}

/** Maps Review Log decision-support candidate ids to void/refund report groupBy values. */
export function groupByFromReportCandidate(
    candidateId: string | undefined
): string | undefined {
    switch (candidateId) {
        case "report_group_employee":
            return "Employee";
        case "report_group_day":
            return "Day";
        case "report_group_location":
            return "Location";
        default:
            return undefined;
    }
}
